// VIN validation, one implementation for the server and the UI (2026-10-21).
// Moved from server/src/ai/image-label-read.js so the header's VIN edit
// (ui/src/App.jsx) and the vin-override route (server/src/routes/ai.js) check
// a VIN exactly the way a VIN read from a customer's photo is checked.

const VIN_SHAPE = /^[A-HJ-NPR-Z0-9]{17}$/
const TRANSLIT = { A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8, J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9, S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9 }
const WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2]

// A VIN's shape: exactly 17 of [A-HJ-NPR-Z0-9] (no I, O or Q), uppercased,
// spaces and dashes stripped. null when it is not one.
export function vinShape(value) {
  const v = String(value ?? '').toUpperCase().replace(/[\s-]/g, '')
  return VIN_SHAPE.test(v) ? v : null
}

// The North American check digit (position 9). Every vehicle sold in the US
// since 1981 passes it. An agent's VIN edit that fails it is kept but marked
// unverified. A photo VIN that fails it is never used (2026-10-21):
// readImageLabels() asks the model to re-read it once, and never "fixes" it.
export function vinCheckDigitOk(vin) {
  if (!VIN_SHAPE.test(vin ?? '')) return false
  let sum = 0
  for (let i = 0; i < 17; i++) {
    const c = vin[i]
    const n = /\d/.test(c) ? Number(c) : TRANSLIT[c]
    sum += n * WEIGHTS[i]
  }
  const r = sum % 11
  return vin[8] === (r === 10 ? 'X' : String(r))
}

// A VIN read from text (2026-10-22, docs/decisions/2026-10-22-agent-subject-
// order-disagreement-vin-check.md): a 17-character value counts only when it
// has a VIN's shape AND passes the check digit, and is not labeled as some
// other id. A PayPal transaction id is 17 letters and digits too ("Transaction
// ID 8AB...", prod contact 8245399); its 9th character is rarely a digit or X,
// and it is labeled. Applies to the message text, a form's VIN field and the
// classifier's VIN entity.
const TEXT_VIN_RE = /(?<![A-Za-z0-9])([A-HJ-NPR-Za-hj-npr-z0-9]{17})(?![A-Za-z0-9])/g
// "Transaction ID", "payment #", "Confirmation number:", "tracking no.",
// "Order ID -" right before the value.
const NON_VIN_LABEL_RE = /\b(?:transaction|txn|payment|confirmation|tracking|order)\s*(?:id|#|number|num|no\.?|code)?\s*[:#-]?\s*$/i
const LABEL_WINDOW = 40

// Whether the value at index in text is labeled as a transaction, payment,
// confirmation, tracking or order id.
export function labeledAsOtherId(text, index) {
  const before = String(text ?? '').slice(Math.max(0, index - LABEL_WINDOW), index)
  return NON_VIN_LABEL_RE.test(before)
}

// A VIN to use from a value already picked out (the classifier's entity, a
// form's VIN field): its shape and its check digit, else null. text: the
// message it came from, so a value labeled as another id there is refused.
export function validTextVin(value, { text = null } = {}) {
  const v = vinShape(value)
  if (!v || !vinCheckDigitOk(v)) return null
  if (text) {
    const s = String(text)
    const i = s.toUpperCase().indexOf(v)
    if (i !== -1 && labeledAsOtherId(s, i)) return null
  }
  return v
}

// The first VIN in a text: a 17-character candidate that is not labeled as
// another id and passes the check digit. null when none does.
export function vinFromText(text) {
  const s = String(text ?? '')
  for (const m of s.matchAll(TEXT_VIN_RE)) {
    const v = m[1].toUpperCase()
    if (!vinCheckDigitOk(v)) continue
    if (labeledAsOtherId(s, m.index)) continue
    return v
  }
  return null
}
